import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  MapPin, Calendar, Users, Trash2, Check, X, 
  ChevronDown, ChevronUp, Edit2, Plus
} from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const TripManager = ({ token }) => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [editingDates, setEditingDates] = useState(null);
  const [newDates, setNewDates] = useState("");

  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async () => {
    try {
      const res = await axios.get(`${API}/trips`);
      setTrips(res.data);
    } catch (error) {
      toast.error("Failed to load trips");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (tripId, newStatus) => {
    try {
      await axios.put(`${API}/admin/trips/${tripId}?status=${newStatus}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Trip status updated to ${newStatus}`);
      fetchTrips();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleDeleteTrip = async (tripId) => {
    if (!confirm("Delete this trip? All votes will be removed.")) return;
    
    try {
      await axios.delete(`${API}/admin/trips/${tripId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Trip deleted");
      fetchTrips();
    } catch (error) {
      toast.error("Failed to delete trip");
    }
  };

  const handleAddDates = async (tripId) => {
    if (!newDates.trim()) return;
    
    const trip = trips.find(t => t.id === tripId);
    const existingDates = trip?.proposed_dates || [];
    const additionalDates = newDates.split(",").map(d => d.trim()).filter(d => d && !existingDates.includes(d));
    
    if (additionalDates.length === 0) {
      toast.error("No new dates to add");
      return;
    }

    try {
      await axios.put(`${API}/admin/trips/${tripId}/dates`, {
        dates: [...existingDates, ...additionalDates]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Dates added!");
      setEditingDates(null);
      setNewDates("");
      fetchTrips();
    } catch (error) {
      toast.error("Failed to add dates");
    }
  };

  const handleRemoveDate = async (tripId, dateToRemove) => {
    const trip = trips.find(t => t.id === tripId);
    const updatedDates = trip.proposed_dates.filter(d => d !== dateToRemove);
    
    try {
      await axios.put(`${API}/admin/trips/${tripId}/dates`, {
        dates: updatedDates
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Date removed");
      fetchTrips();
    } catch (error) {
      toast.error("Failed to remove date");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "voting": return "bg-blue-100 text-blue-700";
      case "confirmed": return "bg-green-100 text-green-700";
      case "cancelled": return "bg-red-100 text-red-700";
      case "completed": return "bg-gray-100 text-gray-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const getVotersForDate = (trip, date) => {
    return trip.votes?.filter(v => v.preferred_dates?.includes(date)) || [];
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-cyan-600" />
          Trip Proposals Management
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-center py-8 text-gray-500">Loading trips...</p>
        ) : trips.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No trip proposals yet</p>
        ) : (
          <div className="space-y-4">
            {trips.map((trip) => (
              <div key={trip.id} className="border rounded-lg overflow-hidden">
                {/* Trip Header */}
                <div 
                  className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                  onClick={() => setExpandedTrip(expandedTrip === trip.id ? null : trip.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold">{trip.title}</h4>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(trip.status)}`}>
                            {trip.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {trip.location}, {trip.country} • {trip.vote_count || 0} votes (min: {trip.min_participants})
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {expandedTrip === trip.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedTrip === trip.id && (
                  <div className="p-4 border-t">
                    {/* Description */}
                    {trip.description && (
                      <p className="text-sm text-gray-600 mb-4">{trip.description}</p>
                    )}

                    {/* Status Controls */}
                    <div className="mb-4">
                      <Label className="text-xs text-gray-500">Change Status</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        <Button
                          size="sm"
                          variant={trip.status === "voting" ? "default" : "outline"}
                          onClick={() => handleStatusChange(trip.id, "voting")}
                          className={trip.status === "voting" ? "bg-blue-500" : ""}
                        >
                          Voting
                        </Button>
                        <Button
                          size="sm"
                          variant={trip.status === "confirmed" ? "default" : "outline"}
                          onClick={() => handleStatusChange(trip.id, "confirmed")}
                          className={trip.status === "confirmed" ? "bg-green-500" : ""}
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Confirmed
                        </Button>
                        <Button
                          size="sm"
                          variant={trip.status === "cancelled" ? "default" : "outline"}
                          onClick={() => handleStatusChange(trip.id, "cancelled")}
                          className={`${trip.status === "cancelled" ? "bg-red-500" : ""}`}
                        >
                          <X className="w-3 h-3 mr-1" />
                          Cancelled
                        </Button>
                        <Button
                          size="sm"
                          variant={trip.status === "completed" ? "default" : "outline"}
                          onClick={() => handleStatusChange(trip.id, "completed")}
                        >
                          Completed
                        </Button>
                      </div>
                    </div>

                    {/* Dates with Voters */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs text-gray-500">Proposed Dates & Voters</Label>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingDates(editingDates === trip.id ? null : trip.id)}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Dates
                        </Button>
                      </div>

                      {/* Add Dates Form */}
                      {editingDates === trip.id && (
                        <div className="flex gap-2 mb-3">
                          <Input
                            value={newDates}
                            onChange={(e) => setNewDates(e.target.value)}
                            placeholder="e.g., April 15-20, April 22-27"
                            className="flex-1"
                          />
                          <Button size="sm" onClick={() => handleAddDates(trip.id)}>
                            Add
                          </Button>
                        </div>
                      )}

                      {/* Date List with Voters */}
                      <div className="space-y-2">
                        {trip.proposed_dates?.map((date) => {
                          const voters = getVotersForDate(trip, date);
                          return (
                            <div key={date} className="p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-gray-400" />
                                  <span className="font-medium">{date}</span>
                                  <span className="text-sm text-gray-500">({voters.length} votes)</span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRemoveDate(trip.id, date)}
                                  className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                              
                              {/* Voter Avatars */}
                              {voters.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {voters.map((voter) => (
                                    <div
                                      key={voter.id}
                                      className="flex items-center gap-1 px-2 py-1 bg-white rounded-full text-xs border"
                                      title={voter.user_name}
                                    >
                                      {voter.user_profile_pic ? (
                                        <img
                                          src={getImageUrl(voter.user_profile_pic)}
                                          alt={voter.user_name}
                                          className="w-5 h-5 rounded-full object-cover"
                                        />
                                      ) : (
                                        <div className="w-5 h-5 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-medium text-xs">
                                          {voter.user_name?.charAt(0) || "?"}
                                        </div>
                                      )}
                                      <span className="text-gray-700">{voter.user_name}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* All Voters Summary */}
                    <div className="mb-4">
                      <Label className="text-xs text-gray-500">All Voters ({trip.vote_count || 0})</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {trip.votes?.map((vote) => (
                          <div
                            key={vote.id}
                            className="flex items-center gap-2 px-3 py-1.5 bg-cyan-50 rounded-full text-sm"
                          >
                            {vote.user_profile_pic ? (
                              <img
                                src={getImageUrl(vote.user_profile_pic)}
                                alt={vote.user_name}
                                className="w-6 h-6 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-cyan-200 flex items-center justify-center text-cyan-700 font-medium text-xs">
                                {vote.user_name?.charAt(0) || "?"}
                              </div>
                            )}
                            <span>{vote.user_name}</span>
                            <span className="text-xs text-gray-500">
                              ({vote.preferred_dates?.length || 0} dates)
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Delete Button */}
                    <div className="pt-4 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteTrip(trip.id)}
                        className="text-red-500 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Trip
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TripManager;
